/**
 * Spor A REF01-M 3.31–3.39: what an XDIS13 bookingsvar / XDIS91 korrespondance
 * answering a received XREF01 carries over. Communicate builds the XML from the
 * stored referral; these helpers only preview the same rules in the UI and
 * validate the form before it is sent.
 *
 * - EpisodeOfCareIdentifier: the referrer's UUID (EpisodeOfCare type "henviser")
 *   without hyphens; "1" only when the referral has none (REF01-M 3.33/3.37).
 * - Receiver: Referrer/Identifier if filled, otherwise Sender/Identifier
 *   (XREF01.pdf p. 8).
 */

export const BOOKING_TEXT_MAX = 70;
export const REPLY_SUBJECT_MAX = 70;
export const REPLY_TEXT_MAX = 3500;

export type ReplyKind = 'XDIS13' | 'XDIS91';

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/** The value Communicate writes into Letter/EpisodeOfCareIdentifier. */
export function replyEpisodeIdentifier(details: Record<string, unknown>): { value: string; fromReferrer: boolean } {
  const episodes = Array.isArray(details.episodesOfCare) ? details.episodesOfCare : [];
  for (const entry of episodes) {
    const ep = record(entry);
    const type = typeof ep?.type === 'string' ? ep.type.trim().toLowerCase() : '';
    const identifier = typeof ep?.identifier === 'string' ? ep.identifier.trim() : '';
    if (type === 'henviser' && identifier) return { value: identifier.replaceAll('-', ''), fromReferrer: true };
  }
  return { value: '1', fromReferrer: false };
}

/** Which party the answer is addressed to. */
export function replyReceiverRule(details: Record<string, unknown>): {
  rule: 'referrer' | 'sender';
  identifier: string | null;
  identifierCode: string | null;
  personName: string | null;
} {
  const referrer = record(details.referrer);
  const identifier = typeof referrer?.identifier === 'string' && referrer.identifier.trim() ? referrer.identifier.trim() : null;
  const identifierCode =
    typeof referrer?.identifierCode === 'string' && referrer.identifierCode.trim() ? referrer.identifierCode.trim() : null;
  const personName = typeof referrer?.personName === 'string' && referrer.personName.trim() ? referrer.personName.trim() : null;
  return identifier
    ? { rule: 'referrer', identifier, identifierCode, personName }
    : { rule: 'sender', identifier: null, identifierCode: null, personName };
}

/** phrases: when given, text must be one of them (DIS13-S 2.4 fixed paraphrases). */
export type BookingForm = { date: string; time: string; text: string; phrases?: string[] };
export type CorrespondenceReplyForm = { subject: string; text: string };

export type ReplyIssue =
  | 'date'
  | 'time'
  | 'text_too_long'
  | 'empty'
  | 'subject_too_long'
  | 'text_missing'
  | 'contact_too_long';

export function bookingIssues(form: BookingForm): ReplyIssue[] {
  const issues: ReplyIssue[] = [];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) issues.push('date');
  if (form.time && !/^(([0-1][0-9])|(2[0-3])):[0-5][0-9]$/.test(form.time)) issues.push('time');
  if (form.text.trim().length > BOOKING_TEXT_MAX) issues.push('text_too_long');
  if (form.phrases && !form.phrases.includes(form.text.trim())) issues.push('text_missing');
  return issues;
}

export function correspondenceReplyIssues(form: CorrespondenceReplyForm): ReplyIssue[] {
  const issues: ReplyIssue[] = [];
  if (!form.subject.trim() && !form.text.trim()) issues.push('empty');
  if (form.subject.trim().length > REPLY_SUBJECT_MAX) issues.push('subject_too_long');
  if (form.text.length > REPLY_TEXT_MAX) issues.push('text_too_long');
  return issues;
}

/**
 * DIS13-S 2.4: the bookingsvar text (Text03, an..70) is one of these fixed
 * "fortrykte" paraphrases; the user cannot write it freely. No date or time in
 * the text: they travel in Confirmation. Kept identical to
 * supabase/functions/communicate-ref/booking-phrases.ts, which refuses any other text.
 */
export const BOOKING_WAITING_GROUPS = ['1', '2', '3', '4', '5'] as const;
export const CONTACT_FIELD_MAX = 35;
export const DEFAULT_CONTACT_TITLE = 'Overlæge';

export function bookingPhrases(organisationName: string): string[] {
  const place = organisationName.trim();
  const at = (text: string) => {
    const full = place ? `${text} hos ${place}` : text;
    return full.length <= BOOKING_TEXT_MAX ? full : text;
  };
  return [
    ...BOOKING_WAITING_GROUPS.map((group) => `Optaget på ventegruppe ${group}`),
    at('Indkaldt til forundersøgelse'),
    at('Indkaldt til undersøgelse'),
    'Tiden er flyttet',
    'Tiden er aflyst',
  ];
}

export type BookingContact = { title: string; name: string };

/**
 * Sender/Contact (2005/08/07 Contact.xsd): Identifier and IdentifierCode are
 * mandatory (Communicate fills them from the sender); OrganisationName,
 * DepartmentName (title) and UnitName (name) are each optional. A title without
 * a name is valid XML, so neither field ever blocks the bookingsvar; only the
 * an..35 length is checked.
 */
export function contactIssues(contact: BookingContact): ReplyIssue[] {
  const issues: ReplyIssue[] = [];
  if (contact.title.trim().length > CONTACT_FIELD_MAX || contact.name.trim().length > CONTACT_FIELD_MAX) {
    issues.push('contact_too_long');
  }
  return issues;
}

/** The contact sent to communicate-ref: title and/or name, omitted when both are empty. */
export function bookingContactPayload(contact: BookingContact): { title?: string; name?: string } | undefined {
  const title = contact.title.trim();
  const name = contact.name.trim();
  if (!title && !name) return undefined;
  return { ...(title ? { title } : {}), ...(name ? { name } : {}) };
}

/** What a sent XDIS13 carried, to prefill "Ret bookingsvar". */
export type SentBooking = {
  statusCode: string;
  date: string;
  time: string;
  text: string;
  contact: BookingContact;
};

function unescapeXml(value: string): string {
  return value
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&quot;', '"')
    .replaceAll('&apos;', "'")
    .replaceAll('&amp;', '&');
}

function firstText(xml: string, tag: string): string {
  const m = new RegExp(`<${tag}\\s*>([^<]*)</${tag}\\s*>`).exec(xml);
  return m ? unescapeXml(m[1]).trim() : '';
}

function section(xml: string, tag: string): string {
  const m = new RegExp(`<${tag}\\s*>([\\s\\S]*?)</${tag}\\s*>`).exec(xml);
  return m ? m[1] : '';
}

export function readSentBooking(xml: string): SentBooking {
  const letter = section(xml, 'Letter');
  const confirmation = section(xml, 'Confirmation');
  const contact = section(section(xml, 'Sender'), 'Contact');
  const time = firstText(confirmation, 'Time');
  return {
    statusCode: firstText(letter, 'StatusCode'),
    date: firstText(confirmation, 'Date'),
    time: time === '00:00' ? '' : time,
    text: firstText(section(xml, 'ClinicalInformation'), 'Text03'),
    contact: { title: firstText(contact, 'DepartmentName'), name: firstText(contact, 'UnitName') },
  };
}
